import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

export default function ExteriorPaintingPage() {
  useEffect(() => {
    document.title = 'Exterior Painting Durbanville | Professional Exterior Painters Western Cape';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional exterior painting services in Durbanville, Western Cape. Expert exterior painters for homes and commercial buildings. Weather-resistant paints and quality workmanship.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'exterior painting Durbanville, exterior painters Western Cape, house painting exterior, weatherproof painting, facade painting');
    }
  }, []);

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/30482bb7c61b6d946fa6260f14a3a578.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-bold mb-6" style={{ fontFamily: '"Pacifico", serif' }}>
            Exterior <span style={{ color: '#e9f638' }}>PAINTING</span>
          </h1>
          <p className="text-2xl md:text-3xl mb-8 font-light">
            Protect and beautify your property with professional exterior painting
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="text-black px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#e9f638' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d4e532'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9f638'}
            >
              Get Free Quote
            </a>
            <a 
              href="#services" 
              className="border-2 border-white hover:bg-white hover:text-black text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Our Services
            </a>
          </div>
        </div>
      </section>

      {/* Exterior Painting Services */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Exterior Painting Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our expert exterior painters in Durbanville provide comprehensive exterior painting solutions that protect and enhance your property's curb appeal.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">House Exterior</h3>
              <p className="text-gray-600">
                Complete exterior house painting including walls, trim, doors, and windows with weather-resistant paints.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-2-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Commercial Buildings</h3>
              <p className="text-gray-600">
                Professional exterior painting for commercial properties, offices, and industrial buildings with durable finishes.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-shield-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Weather Protection</h3>
              <p className="text-gray-600">
                Specialized weatherproof coatings and paints designed to withstand South African climate conditions.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-tools-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Surface Preparation</h3>
              <p className="text-gray-600">
                Thorough surface preparation including pressure washing, scraping, sanding, and priming for optimal results.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-drop-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Waterproofing</h3>
              <p className="text-gray-600">
                Advanced waterproofing solutions to protect your exterior walls from moisture damage and leaks.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-time-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Maintenance Plans</h3>
              <p className="text-gray-600">
                Regular maintenance painting schedules to keep your exterior looking fresh and protected year-round.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits of Exterior Painting */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Stunning%20Durbanville%20home%20exterior%20with%20fresh%20professional%20paint%20job%2C%20beautiful%20facade%2C%20modern%20architecture%2C%20well-maintained%20garden%2C%20South%20African%20residential%20property%2C%20excellent%20curb%20appeal%2C%20quality%20finish&width=600&height=400&seq=exterior-benefits&orientation=landscape"
                alt="Professional exterior painting results"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">Benefits of Professional Exterior Painting</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Weather Protection</h3>
                    <p className="text-gray-600">High-quality exterior paints provide essential protection against UV rays, rain, wind, and temperature fluctuations.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Increased Property Value</h3>
                    <p className="text-gray-600">Fresh exterior paint significantly boosts curb appeal and can increase your property value by up to 10%.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Long-lasting Results</h3>
                    <p className="text-gray-600">Professional exterior painting with quality materials can last 7-10 years with proper maintenance.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Structural Protection</h3>
                    <p className="text-gray-600">Quality exterior paint acts as a barrier against moisture, preventing wood rot, rust, and structural damage.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Our Other Painting Services</h2>
            <p className="text-xl text-gray-600">
              Explore our complete range of professional painting services in Durbanville
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="/interior-painting" className="group bg-gray-50 p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Interior Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Professional interior painting for homes and offices.</p>
            </a>

            <a href="/roof-painting" className="group bg-gray-50 p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-2-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Roof Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Specialized roof painting and waterproofing services.</p>
            </a>

            <a href="/commercial-painting" className="group bg-gray-50 p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Commercial Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Large-scale commercial and industrial painting projects.</p>
            </a>

            <a href="/residential-painting" className="group bg-gray-50 p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Residential Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Complete residential painting solutions for your home.</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Get Your Exterior Painting Quote</h2>
          <p className="text-xl text-gray-300 mb-12">
            Ready to enhance your property's exterior? Get a free quote from Durbanville's best exterior painters.
          </p>
          <ContactForm />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <TestimonialsSlider />
        </div>
      </section>

      {/* Elfsight AI Chatbot */}
      <script src="https://elfsightcdn.com/platform.js" async></script>
      <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>

      {/* ProveSource Code */}
      <script dangerouslySetInnerHTML={{
        __html: `!function(o,i){window.provesrc&&window.console&&console.error&&console.error("ProveSource is included twice in this page."),provesrc=window.provesrc={dq:[],display:function(){this.dq.push(arguments)}},o._provesrcAsyncInit=function(){provesrc.init({apiKey:"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2NvdW50SWQiOiI2NmQzYzc2NjQ3MWQwZDE2NTI2N2VkZDAiLCJpYXQiOjE3MjUxNTUxNzR9.F74G52UprvsGoyXgXsaIMG-PS-c2G7yr84Ut5rrQ4cg",v:"0.0.4"})};var r=i.createElement("script");r.type="text/javascript",r.async=!0,r["ch"+"ar"+"set"]="UTF-8",r.src="https://cdn.provesrc.com/provesrc.js";var e=i.getElementsByTagName("script")[0];e.parentNode.insertBefore(r,e)}(window,document);`
      }} />

      <Footer />
    </>
  );
}
